/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { fetchDashboardData, updateOrderStatus } from '../services/api';
import { createPortal } from 'react-dom';
import 'remixicon/fonts/remixicon.css';

const IMG_BASE = 'https://perfumebackend.escentrum.com';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [updatingId, setUpdatingId] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusPopup, setStatusPopup] = useState({ show: false, type: '', message: '' });

    const containerRef = useRef(null);
    const rowsRef = useRef([]);

    useEffect(() => { loadOrders(); }, []);
    useEffect(() => { filterOrders(); }, [orders, activeFilter, searchTerm]);

    const loadOrders = async () => {
        setLoading(true);
        const data = await fetchDashboardData();
        if (data?.allOrders) setOrders(data.allOrders);
        setLoading(false);
    };

    const filterOrders = () => {
        let filtered = [...orders];
        if (activeFilter !== 'all') {
            filtered = filtered.filter(o => o.status?.toLowerCase() === activeFilter.toLowerCase());
        }
        if (searchTerm) {
            const t = searchTerm.toLowerCase();
            filtered = filtered.filter(o =>
                o.orderNumber?.toLowerCase().includes(t) ||
                o.userId?.fullName?.toLowerCase().includes(t) ||
                o._id?.toLowerCase().includes(t) ||
                o.paymentMethod?.toLowerCase().includes(t) ||
                String(o.subtotal || '').includes(t) ||
                o.items?.some(item => item.titleSnapshot?.toLowerCase().includes(t))
            );
        }
        setFilteredOrders(filtered);
    };

    const handleStatusUpdate = async (id, newStatus) => {
        setUpdatingId(id);
        try {
            const response = await updateOrderStatus(id, newStatus);
            if (response.message?.toLowerCase().includes('updated')) {
                setOrders(prev => prev.map(o => o._id === id ? { ...o, status: newStatus } : o));
                showPopup('success', `Order marked as ${newStatus}`);
            } else {
                showPopup('error', 'Failed to update status');
            }
        } catch {
            showPopup('error', 'Connection error');
        } finally {
            setUpdatingId(null);
        }
    };

    const showPopup = (type, message) => {
        setStatusPopup({ show: true, type, message });
        setTimeout(() => setStatusPopup({ show: false, type: '', message: '' }), 3000);
    };

    useEffect(() => {
        if (!loading && filteredOrders.length > 0) {
            gsap.fromTo(rowsRef.current,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, stagger: 0.05, duration: 0.6, ease: 'power3.out' }
            );
        }
    }, [loading, filteredOrders.length]);

    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400';
            case 'pending':   return 'bg-amber-500/10 border-amber-500/20 text-amber-400';
            case 'shipped':   return 'bg-blue-500/10 border-blue-500/20 text-blue-400';
            case 'cancelled': return 'bg-[#c82465]/10 border-[#c82465]/20 text-[#c82465]';
            default:          return 'bg-white/5 border-white/10 text-white/40';
        }
    };

    const getDot = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return 'bg-emerald-400';
            case 'pending':   return 'bg-amber-400 animate-pulse';
            case 'shipped':   return 'bg-blue-400 animate-pulse';
            case 'cancelled': return 'bg-[#e0527a]';
            default:          return 'bg-white/30';
        }
    };

    /* ─── product thumbnail helper ─── */
    const ProductThumb = ({ src, alt, size = 'sm' }) => {
        const [err, setErr] = useState(false);
        const dim = size === 'lg'
            ? 'w-20 h-20 sm:w-24 sm:h-24 rounded-2xl'
            : 'w-10 h-10 rounded-xl';
        const url = src ? (src.startsWith('http') ? src : `${IMG_BASE}${src}`) : '';
        return (
            <div className={`${dim} overflow-hidden flex-shrink-0 border border-white/10 bg-white/5 flex items-center justify-center`}>
                {url && !err
                    ? <img src={url} alt={alt} onError={() => setErr(true)} className="w-full h-full object-cover" />
                    : <i className="ri-image-2-line text-white/20 text-lg"></i>
                }
            </div>
        );
    };

    return (
        <div ref={containerRef} className="pt-2 pb-32 space-y-6 text-white selection:bg-[#c82465]/30">

            {/* ══ HEADER ══ */}
            <div className="relative overflow-hidden bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-5 sm:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)] group">
                <div className="absolute inset-0 bg-gradient-to-r from-[#c82465]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 relative z-10">
                    <div>
                        <h1 className="text-2xl sm:text-4xl font-luxury italic text-white tracking-tight flex items-center gap-3">
                            <i className="ri-shopping-bag-3-fill text-[#c82465] text-2xl sm:text-4xl"></i>
                            Order <span className="bg-gradient-to-r from-[#6b0f3a] to-[#c82465] bg-clip-text text-transparent">Management</span>
                        </h1>
                        <p className="text-white/30 mt-1.5 tracking-[0.3em] uppercase text-[9px] font-bold">Overseeing the flow of global luxury essence</p>
                    </div>
                    <div className="relative w-full sm:w-auto">
                        <i className="ri-search-2-line absolute left-4 top-1/2 -translate-y-1/2 text-white/20 text-sm"></i>
                        <input
                            type="text"
                            placeholder="Search orders..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full sm:w-64 bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-11 pr-5 text-[10px] font-bold tracking-[0.15em] outline-none focus:border-[#c82465]/30 focus:bg-white/[0.08] transition-all placeholder:text-white/20 text-white/70"
                        />
                    </div>
                </div>
            </div>

            {/* ══ FILTER TABS ══ */}
            <div className="flex items-center gap-2 p-1.5 bg-white/[0.02] border border-white/5 rounded-2xl w-full overflow-x-auto no-scrollbar backdrop-blur-xl">
                {['all', 'pending', 'delivered', 'shipped', 'cancelled'].map(f => (
                    <button key={f} onClick={() => setActiveFilter(f)}
                        className={`flex-shrink-0 px-5 sm:px-8 py-2.5 rounded-xl text-[9px] font-bold uppercase tracking-[0.2em] transition-all duration-500 ${activeFilter === f
                            ? 'bg-gradient-to-r from-[#6b0f3a] to-[#c82465] text-white shadow-[0_10px_30px_rgba(192,38,211,0.3)]'
                            : 'text-white/30 hover:text-white hover:bg-white/5'}`}>
                        {f}
                    </button>
                ))}
            </div>

            {/* ══ ORDERS LIST ══ */}
            <div className="space-y-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-[40vh]">
                        <div className="w-10 h-10 border-2 border-[#c82465]/20 border-t-[#c82465] rounded-full animate-spin"></div>
                        <p className="mt-6 text-[#c82465]/40 text-[10px] font-bold uppercase tracking-[0.5em] italic animate-pulse">Syncing Orders...</p>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="bg-white/[0.02] border border-white/5 rounded-[2rem] p-16 sm:p-24 text-center">
                        <i className="ri-inbox-archive-line text-5xl text-white/5 mb-4 block"></i>
                        <p className="text-white/20 text-[10px] font-bold uppercase tracking-[0.4em]">No orders found</p>
                    </div>
                ) : (
                    filteredOrders.map((order, index) => {
                        const firstImg = order.items?.[0]?.imageSnapshot;
                        const extraItems = (order.items?.length || 0) - 1;
                        return (
                            <div
                                key={order._id}
                                ref={el => rowsRef.current[index] = el}
                                className="group relative bg-gradient-to-r from-white/[0.03] to-transparent border border-white/5 rounded-[1.5rem] sm:rounded-[2rem] p-4 sm:p-6 hover:border-[#c82465]/20 hover:bg-white/[0.05] transition-all duration-500 shadow-xl overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-40 h-40 bg-[#c82465]/5 blur-[60px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000 -mr-10 -mt-10 pointer-events-none"></div>

                                {/* ── Top row: product thumb + name + status ── */}
                                <div className="flex items-start gap-3 sm:gap-4 mb-4 relative z-10">
                                    {/* Product image */}
                                    <ProductThumb src={firstImg} alt={order.items?.[0]?.titleSnapshot} size="sm" />

                                    {/* Customer + order ref */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[8px] font-bold text-[#c82465]/50 uppercase tracking-[0.3em]">Consignee</p>
                                        <h4 className="text-base sm:text-lg font-luxury italic text-white truncate leading-tight">
                                            {order.userId?.fullName || 'Anonymous'}
                                        </h4>
                                        <p className="text-[9px] text-white/30 font-bold tracking-widest uppercase mt-0.5 truncate">{order.orderNumber}</p>
                                    </div>

                                    {/* Status badge */}
                                    <span className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[8px] font-bold uppercase tracking-wider border ${getStatusStyle(order.status)}`}>
                                        <div className={`w-1 h-1 rounded-full ${getDot(order.status)}`}></div>
                                        <span className="hidden xs:inline">{order.status}</span>
                                    </span>
                                </div>

                                {/* ── Middle row: stats grid ── */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 relative z-10">
                                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                                        <p className="text-[7px] sm:text-[8px] font-bold text-white/20 uppercase tracking-[0.2em] mb-1">Valuation</p>
                                        <p className="text-sm sm:text-base font-luxury italic text-white">Rs {order.subtotal?.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                                        <p className="text-[7px] sm:text-[8px] font-bold text-white/20 uppercase tracking-[0.2em] mb-1">Date</p>
                                        <p className="text-[10px] font-bold text-white/50 truncate">
                                            {new Date(order.createdAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                                        <p className="text-[7px] sm:text-[8px] font-bold text-white/20 uppercase tracking-[0.2em] mb-1">Payment</p>
                                        <p className="text-[10px] font-bold text-white/50 truncate uppercase">{order.paymentMethod || 'N/A'}</p>
                                    </div>
                                    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                                        <p className="text-[7px] sm:text-[8px] font-bold text-white/20 uppercase tracking-[0.2em] mb-1">Items</p>
                                        <p className="text-[10px] font-bold text-white/50">{order.items?.length || 0} product{(order.items?.length || 0) !== 1 ? 's' : ''}</p>
                                    </div>
                                </div>

                                {/* ── Extra product thumbnails (if multiple items) ── */}
                                {(order.items?.length || 0) > 1 && (
                                    <div className="flex items-center gap-2 mb-4 relative z-10 flex-wrap">
                                        {order.items.slice(0, 5).map((item, i) => (
                                            <ProductThumb key={i} src={item.imageSnapshot} alt={item.titleSnapshot} size="sm" />
                                        ))}
                                        {order.items.length > 5 && (
                                            <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                                                <span className="text-[9px] font-bold text-white/40">+{order.items.length - 5}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── Bottom row: status select + view button ── */}
                                <div className="flex items-center gap-3 relative z-10">
                                    <div className="relative flex-1 sm:flex-none">
                                        <select
                                            value={order.status}
                                            disabled={updatingId === order._id}
                                            onChange={e => handleStatusUpdate(order._id, e.target.value)}
                                            className={`w-full sm:w-auto appearance-none bg-white/5 border rounded-xl px-4 py-2.5 pr-8 text-[9px] font-bold uppercase tracking-[0.2em] outline-none hover:border-[#c82465]/30 transition-all cursor-pointer disabled:opacity-50 ${getStatusStyle(order.status)}`}
                                        >
                                            <option value="pending"   className="bg-[#0a0a0a]">Pending</option>
                                            <option value="shipped"   className="bg-[#0a0a0a]">Shipped</option>
                                            <option value="delivered" className="bg-[#0a0a0a]">Delivered</option>
                                            <option value="cancelled" className="bg-[#0a0a0a]">Cancelled</option>
                                        </select>
                                        <i className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-sm ${updatingId === order._id ? 'ri-loader-4-line animate-spin' : 'ri-arrow-down-s-line'} text-white/30`}></i>
                                    </div>
                                    <button
                                        onClick={() => setSelectedOrder(order)}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/30 hover:text-[#c82465] hover:bg-white/10 transition-all text-[9px] font-bold uppercase tracking-widest whitespace-nowrap"
                                    >
                                        <i className="ri-eye-line text-sm"></i>
                                        <span className="hidden sm:inline">View</span>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* ══ TOAST POPUP ══ */}
            <AnimatePresence>
                {statusPopup.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        className={`fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-auto z-[200] px-6 py-4 rounded-2xl border backdrop-blur-3xl shadow-2xl flex items-center gap-4 ${statusPopup.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-[#c82465]/10 border-[#c82465]/20 text-[#c82465]'}`}
                    >
                        <i className={`text-2xl ${statusPopup.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`}></i>
                        <p className="text-sm font-medium italic">{statusPopup.message}</p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ══ ORDER DETAIL MODAL ══ */}
            {createPortal(
                <AnimatePresence>
                    {selectedOrder && (
                        <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6" style={{ zIndex: 999999999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
                            <motion.div
                                key="backdrop"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                onClick={() => setSelectedOrder(null)}
                                className="absolute inset-0 bg-black/80 backdrop-blur-[30px]"
                            />
                            <motion.div
                                key="modal"
                            initial={{ scale: 0.9, y: 50, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.9, y: 50, opacity: 0 }}
                            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                            className="relative z-10 w-full sm:max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
                        >
                            {/* Modal Header */}
                            <div className="flex items-start justify-between p-5 sm:p-8 border-b border-white/5 flex-shrink-0">
                                <div>
                                    <p className="text-[9px] font-bold text-[#c82465] uppercase tracking-[0.4em] mb-1">Detailed Manifesto</p>
                                    <h2 className="text-xl sm:text-3xl font-luxury italic text-white leading-tight">{selectedOrder.orderNumber}</h2>
                                    <div className="flex flex-wrap items-center gap-3 mt-3">
                                        <span className={`px-3 py-1 rounded-full text-[8px] font-bold uppercase tracking-wider border flex items-center gap-1.5 ${getStatusStyle(selectedOrder.status)}`}>
                                            <div className={`w-1 h-1 rounded-full ${getDot(selectedOrder.status)}`}></div>
                                            {selectedOrder.status}
                                        </span>
                                        <p className="text-[9px] text-white/20 font-bold uppercase tracking-widest">{selectedOrder.paymentMethod}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/40 hover:bg-[#c82465] hover:text-white transition-all"
                                >
                                    <i className="ri-close-line text-lg"></i>
                                </button>
                            </div>

                            {/* Scrollable body */}
                            <div className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-3 custom-scrollbar">
                                {selectedOrder.items?.map((item, i) => (
                                    <div key={i} className="flex items-center gap-4 sm:gap-5 p-4 bg-white/[0.03] rounded-2xl border border-white/5 group/item">
                                        {/* ── Big product image in modal ── */}
                                        <ProductThumb src={item.imageSnapshot} alt={item.titleSnapshot} size="lg" />
                                        <div className="flex-1 min-w-0">
                                            <h5 className="text-sm sm:text-base font-luxury italic text-white group-hover/item:text-[#e0527a] transition-colors leading-tight">{item.titleSnapshot}</h5>
                                            <p className="text-[9px] text-white/30 font-bold uppercase tracking-widest mt-1">
                                                QTY: {item.qty} × Rs {item.priceSnapshot?.toLocaleString()}
                                            </p>
                                        </div>
                                        <p className="text-base sm:text-xl font-luxury text-[#e0527a] italic flex-shrink-0">
                                            Rs {(item.qty * item.priceSnapshot)?.toLocaleString()}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Modal Footer */}
                            <div className="border-t border-white/5 p-5 sm:p-8 flex-shrink-0 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 bg-white/[0.01]">
                                <div>
                                    <p className="text-[8px] font-bold text-white/20 uppercase tracking-[0.3em] mb-1">Shipping Archive</p>
                                    <p className="text-xs text-white/50 leading-relaxed font-medium max-w-xs">{selectedOrder.shippingAddress || 'N/A'}</p>
                                </div>
                                <div className="sm:text-right">
                                    <p className="text-[8px] font-bold text-[#c82465]/40 uppercase tracking-[0.3em] mb-1">Final Valuation</p>
                                    <p className="text-2xl sm:text-3xl font-luxury italic text-white">Rs {selectedOrder.subtotal?.toLocaleString()}</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default Orders;
